import React, { useState } from 'react';
import { kpiPortalApi, portalSession } from '../lib/api';

/**
 * Everything an employee must pass before the portal opens.
 *
 * The QR is permanent and printed, so it is treated as an identifier only.
 * First scan  -> prove ownership of the registered email/mobile by OTP, then
 *                choose a password.
 * Later scans -> password.
 * Forgot      -> same OTP proof, then a new password.
 */
export default function PortalGate({ token, state, onAuthed }) {
  // 'login' | 'activate' | 'forgot'
  const [mode, setMode] = useState(state.state === 'needs_activation' ? 'activate' : 'login');
  const [step, setStep] = useState('choose');   // choose -> code -> password
  const [channel, setChannel] = useState(state.has_email ? 'email' : 'mobile');
  const [sentTo, setSentTo] = useState('');
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const purpose = mode === 'activate' ? 'activate' : 'reset';
  const fail = (e, fallback) => setErr(e?.response?.data?.detail || fallback);

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#EDF0F6', display: 'flex',
                  alignItems: 'flex-start', justifyContent: 'center',
                  padding: '28px 12px', fontFamily: 'Segoe UI,Helvetica,Arial,sans-serif' }}>
      <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 16,
                    overflow: 'hidden', boxShadow: '0 6px 26px rgba(16,36,104,.12)' }}>
        <div style={{ background: 'linear-gradient(135deg,#0C1B57,#2563EB)', color: '#fff',
                      padding: '22px 22px 20px' }}>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: .85 }}>AAMPL · EMPLOYEE PORTAL</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginTop: 5 }}>{state.name || 'Welcome'}</div>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );

  const inp = {
    width: '100%', boxSizing: 'border-box', border: '1px solid #DDE3EE', borderRadius: 10,
    padding: '12px 14px', fontSize: 16, outlineColor: '#2563EB',
  };
  const btn = (bg, disabled) => ({
    width: '100%', marginTop: 14, background: bg, color: '#fff', border: 'none',
    borderRadius: 10, padding: 14, fontSize: 16, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? .55 : 1,
  });
  const link = { background: 'none', border: 'none', color: '#2563EB', cursor: 'pointer',
                 fontSize: 13, padding: 0 };
  const errBox = err ? (
    <div style={{ background: '#FDECEE', border: '1px solid #F5C2C7', color: '#B02A37',
                  fontSize: 13, borderRadius: 8, padding: '10px 12px', margin: '12px 0' }}>{err}</div>
  ) : null;

  const finish = (session) => { portalSession.set(token, session); onAuthed(); };

  // ---------------------------------------------------------------- login --
  const doLogin = async () => {
    if (!pw) { setErr('Enter your password'); return; }
    setBusy(true); setErr('');
    try {
      const r = await kpiPortalApi.login(token, pw);
      finish(r.data.session);
    } catch (e) { fail(e, 'Could not sign in'); } finally { setBusy(false); }
  };

  if (mode === 'login') {
    return wrap(
      <>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#11182B' }}>Enter your password</div>
        <div style={{ fontSize: 12.5, color: '#5A6377', margin: '4px 0 16px' }}>
          This QR is linked to your record. Your password keeps it private.
        </div>
        <input type="password" value={pw} autoFocus autoComplete="current-password"
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doLogin()}
          placeholder="Password" style={inp} />
        {errBox}
        <button onClick={doLogin} disabled={busy} style={btn('#2563EB', busy)}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button style={link} onClick={() => { setMode('forgot'); setStep('choose'); setErr(''); setPw(''); }}>
            Forgot password?
          </button>
        </div>
      </>,
    );
  }

  // ------------------------------------------------- activate / forgot -----
  const sendCode = async () => {
    setBusy(true); setErr('');
    try {
      const r = await kpiPortalApi.sendOtp(token, purpose, channel);
      setSentTo(r.data.sent_to); setStep('code');
    } catch (e) { fail(e, 'Could not send the code'); } finally { setBusy(false); }
  };

  const checkCode = async () => {
    if (code.trim().length !== 6) { setErr('Enter the 6-digit code'); return; }
    setBusy(true); setErr('');
    try {
      const r = await kpiPortalApi.verifyOtp(token, purpose, code.trim());
      setTicket(r.data.ticket); setStep('password');
    } catch (e) { fail(e, 'Could not verify the code'); } finally { setBusy(false); }
  };

  const savePassword = async () => {
    if (pw.length < (state.min_password || 8)) {
      setErr(`Password must be at least ${state.min_password || 8} characters`); return;
    }
    if (pw !== pw2) { setErr('Both passwords must match'); return; }
    setBusy(true); setErr('');
    try {
      const r = await kpiPortalApi.setPassword(token, ticket, pw);
      finish(r.data.session);
    } catch (e) { fail(e, 'Could not save your password'); } finally { setBusy(false); }
  };

  const heading = mode === 'activate' ? 'Set up your account' : 'Reset your password';

  return wrap(
    <>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#11182B' }}>{heading}</div>

      {step === 'choose' && (
        <>
          <div style={{ fontSize: 12.5, color: '#5A6377', margin: '4px 0 16px' }}>
            {mode === 'activate'
              ? 'This is your first sign-in. We will send a code to confirm it is you.'
              : 'We will send a code so you can choose a new password.'}
          </div>

          {state.has_email && (
            <button onClick={() => setChannel('email')}
              style={{ width: '100%', textAlign: 'left', marginBottom: 10, cursor: 'pointer',
                       background: channel === 'email' ? '#EEF3FF' : '#fff',
                       border: `1px solid ${channel === 'email' ? '#2563EB' : '#DDE3EE'}`,
                       borderRadius: 12, padding: '13px 15px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#11182B' }}>✉️ Email</div>
              <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>{state.email_hint}</div>
            </button>
          )}

          {state.has_mobile && (
            <button onClick={() => state.sms_available && setChannel('mobile')}
              disabled={!state.sms_available}
              style={{ width: '100%', textAlign: 'left', marginBottom: 10,
                       cursor: state.sms_available ? 'pointer' : 'not-allowed',
                       opacity: state.sms_available ? 1 : .55,
                       background: channel === 'mobile' ? '#EEF3FF' : '#fff',
                       border: `1px solid ${channel === 'mobile' ? '#2563EB' : '#DDE3EE'}`,
                       borderRadius: 12, padding: '13px 15px' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#11182B' }}>📱 Mobile (SMS)</div>
              <div style={{ fontSize: 12, color: '#5A6377', marginTop: 2 }}>
                {state.mobile_hint}{state.sms_available ? '' : ' · not available yet'}
              </div>
            </button>
          )}

          {!state.has_email && !state.has_mobile && (
            <div style={{ background: '#FFF7E6', border: '1px solid #FDD08A', color: '#8A5A12',
                          fontSize: 13, borderRadius: 8, padding: '11px 13px', margin: '4px 0' }}>
              No email or mobile is registered on your record, so we cannot send a code.
              Please ask HR to add your contact details.
            </div>
          )}

          {errBox}
          <button onClick={sendCode} disabled={busy || (!state.has_email && !state.has_mobile)}
            style={btn('#2563EB', busy || (!state.has_email && !state.has_mobile))}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </>
      )}

      {step === 'code' && (
        <>
          <div style={{ fontSize: 12.5, color: '#5A6377', margin: '4px 0 16px' }}>
            We sent a 6-digit code to <b>{sentTo}</b>. It expires in {state.otp_ttl_minutes || 10} minutes.
          </div>
          <input value={code} autoFocus inputMode="numeric" maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && checkCode()}
            placeholder="······"
            style={{ ...inp, textAlign: 'center', fontSize: 26, letterSpacing: 10, fontWeight: 700 }} />
          {errBox}
          <button onClick={checkCode} disabled={busy} style={btn('#2563EB', busy)}>
            {busy ? 'Checking…' : 'Verify code'}
          </button>
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button style={link} onClick={() => { setStep('choose'); setCode(''); setErr(''); }}>
              Send it again
            </button>
          </div>
        </>
      )}

      {step === 'password' && (
        <>
          <div style={{ fontSize: 12.5, color: '#5A6377', margin: '4px 0 16px' }}>
            Choose a password. You will use it every time you scan your QR.
          </div>
          <input type="password" value={pw} autoFocus autoComplete="new-password"
            onChange={(e) => setPw(e.target.value)}
            placeholder={`New password (min ${state.min_password || 8})`} style={inp} />
          <input type="password" value={pw2} autoComplete="new-password"
            onChange={(e) => setPw2(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && savePassword()}
            placeholder="Repeat password" style={{ ...inp, marginTop: 10 }} />
          {errBox}
          <button onClick={savePassword} disabled={busy} style={btn('#15A36E', busy)}>
            {busy ? 'Saving…' : 'Save and continue'}
          </button>
        </>
      )}

      {mode === 'forgot' && (
        <div style={{ textAlign: 'center', marginTop: 14 }}>
          <button style={link} onClick={() => { setMode('login'); setErr(''); setStep('choose'); }}>
            ← Back to sign in
          </button>
        </div>
      )}
    </>,
  );
}
